import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { useChangePassword, useLogout } from '../lib/session';
import { Alert, Button, Field, LanguagePicker, Logo } from '../components/ui';
import { TwoFactor } from '../components/TwoFactor';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordPage({ forced }: { forced: boolean }) {
  const { t } = useI18n();

  // Built per render: the messages are translated, and a schema created once
  // at import time would be stuck in the language that was active then.
  const schema = z
    .object({
      currentPassword: z.string().min(1, t('password.currentRequired')),
      newPassword: z.string().min(12, t('password.tooShort')),
      confirmPassword: z.string().min(1, t('password.repeatRequired')),
    })
    .refine((v) => v.newPassword === v.confirmPassword, {
      path: ['confirmPassword'],
      message: t('password.mismatch'),
    })
    .refine((v) => v.newPassword !== v.currentPassword, {
      path: ['newPassword'],
      message: t('password.reused'),
    });

  const changePassword = useChangePassword();
  const logout = useLogout();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const errorText = useApiError();
  const error = changePassword.error instanceof ApiError ? changePassword.error : null;

  return (
    <main className="grid min-h-dvh place-items-center px-5 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-[max(2.5rem,env(safe-area-inset-top))]">
      <div className="w-full max-w-sm">
        <div className="mb-9 flex flex-col items-center gap-5 text-center">
          {/* Only on the forced screen, which stands alone. Inside the portal
              the sidebar is already showing the mark. */}
          {forced && <Logo withWordmark size="lg" />}
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink">
              {forced ? t('password.setNew') : t('security.title')}
            </h1>
            {forced && (
              <p className="mt-1.5 text-sm text-ink-dim">
                {t('password.forced')}
              </p>
            )}
          </div>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit((values) =>
            changePassword.mutate({
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            }),
          )}
          className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-6"
        >
          {error && <Alert>{errorText(error) ?? error.message}</Alert>}

          <Field
            label={t('password.current')}
            type="password"
            autoComplete="current-password"
            autoFocus
            error={formState.errors.currentPassword?.message}
            {...register('currentPassword')}
          />
          <Field
            label={t('password.new')}
            type="password"
            autoComplete="new-password"
            hint={t('password.rule')}
            error={formState.errors.newPassword?.message}
            {...register('newPassword')}
          />
          <Field
            label={t('password.repeat')}
            type="password"
            autoComplete="new-password"
            error={formState.errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button type="submit" loading={changePassword.isPending} className="mt-1">
            {t('password.submit')}
          </Button>

          {forced && (
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="min-h-11 text-xs text-ink-faint transition hover:text-ink-dim"
            >
              {t('password.signOutInstead')}
            </button>
          )}
        </form>

        {/* Not during the forced change: one thing at a time, and the account
            cannot set up a code before it has a password of its own. */}
        {!forced && <div className="mt-4"><TwoFactor /></div>}

        {/* Same reason as on the sign-in screen: nothing else is reachable. */}
        {forced && <LanguagePicker className="mt-4 justify-center" />}
      </div>
    </main>
  );
}
